import { createColorTransform, getCameraProfile, getColorSpace, isSupportedTransferFunction, saturation, } from '@limonade/color-science';
import { createPaletteColorCorrection, measurePaletteMatch, CorrectionEstimator, HueDistributionAnalyzer, LuminanceDistributionAnalyzer, PerceptualHistogram, SaturationDistributionAnalyzer, TonalDistributionAnalyzer, createTonalCorrection, } from '@limonade/matching';
import { frameToImageData } from './frame.js';
import { semanticSkinWeight } from './semantic.js';
import { createPerceptualMetric, createWorkingTransforms } from './colorAnalysis.js';
import { createPCEPaletteFrame, getPCEPalettePreset } from './palettePresets.js';
const MAX_SAMPLES = 50_000;
const round = (value, digits = 4) => Number(value.toFixed(digits));
const clamp01 = (value) => Math.max(0, Math.min(1, value));
function sampleBudget(options) {
    if (options?.sampling?.maxSamples)
        return Math.max(1, options.sampling.maxSamples);
    if (options?.sampling?.quality === 'realtime')
        return 6_000;
    if (options?.sampling?.quality === 'balanced')
        return 18_000;
    return MAX_SAMPLES;
}
function blendOptionalWhitePoint(previous, current, weight) {
    if (!previous)
        return current;
    if (!current)
        return previous;
    const blend = (first, second, digits = 5) => round(first * (1 - weight) + second * weight, digits);
    const blendSample = (first, second) => ({
        r: blend(first.r, second.r),
        g: blend(first.g, second.g),
        b: blend(first.b, second.b),
        sampleCount: Math.round(first.sampleCount * (1 - weight) + second.sampleCount * weight),
        confidence: blend(first.confidence, second.confidence, 4),
    });
    return {
        reference: blendSample(previous.reference, current.reference),
        source: blendSample(previous.source, current.source),
        channelGains: current.channelGains.map((value, index) => blend(previous.channelGains[index] ?? value, value, 6)),
        confidence: blend(previous.confidence, current.confidence, 4),
    };
}
function blendOptionalBlackPoint(previous, current, weight) {
    if (!previous)
        return current;
    if (!current)
        return previous;
    const blend = (first, second, digits = 5) => round(first * (1 - weight) + second * weight, digits);
    const blendSample = (first, second) => ({
        r: blend(first.r, second.r),
        g: blend(first.g, second.g),
        b: blend(first.b, second.b),
        sampleCount: Math.round(first.sampleCount * (1 - weight) + second.sampleCount * weight),
        confidence: blend(first.confidence, second.confidence, 4),
    });
    return {
        reference: blendSample(previous.reference, current.reference),
        source: blendSample(previous.source, current.source),
        channelOffsets: current.channelOffsets.map((value, index) => blend(previous.channelOffsets[index] ?? value, value, 6)),
        confidence: blend(previous.confidence, current.confidence, 4),
    };
}
function blendOptionalSaturationMatch(previous, current, weight) {
    if (!previous)
        return current;
    if (!current)
        return previous;
    return {
        referenceMean: round(previous.referenceMean * (1 - weight) + current.referenceMean * weight, 5),
        sourceMean: round(previous.sourceMean * (1 - weight) + current.sourceMean * weight, 5),
        saturationRatio: round(previous.saturationRatio * (1 - weight) + current.saturationRatio * weight, 6),
        confidence: round(previous.confidence * (1 - weight) + current.confidence * weight, 4),
    };
}
function blendOptionalSkinToneMatch(previous, current, weight) {
    if (!previous)
        return current;
    if (!current)
        return previous;
    const blend = (first, second, digits = 5) => round(first * (1 - weight) + second * weight, digits);
    const blendSample = (first, second) => ({
        r: blend(first.r, second.r),
        g: blend(first.g, second.g),
        b: blend(first.b, second.b),
        sampleCount: Math.round(first.sampleCount * (1 - weight) + second.sampleCount * weight),
        confidence: blend(first.confidence, second.confidence, 4),
    });
    return {
        reference: blendSample(previous.reference, current.reference),
        source: blendSample(previous.source, current.source),
        channelPower: current.channelPower.map((value, index) => blend(previous.channelPower[index] ?? value, value, 6)),
        confidence: blend(previous.confidence, current.confidence, 4),
    };
}
function blendOptionalColorVectorMatch(previous, current, weight) {
    if (!previous)
        return current;
    if (!current)
        return previous;
    const vectors = current.vectors.map((vector, index) => {
        const old = previous.vectors[index];
        return {
            ...vector,
            referenceHue: old?.referenceHue === null || old?.referenceHue === undefined || vector.referenceHue === null
                ? vector.referenceHue
                : round(old.referenceHue * (1 - weight) + vector.referenceHue * weight, 4),
            sourceHue: old?.sourceHue === null || old?.sourceHue === undefined || vector.sourceHue === null
                ? vector.sourceHue
                : round(old.sourceHue * (1 - weight) + vector.sourceHue * weight, 4),
            hueShiftDegrees: round((old?.hueShiftDegrees ?? vector.hueShiftDegrees) * (1 - weight) + vector.hueShiftDegrees * weight, 4),
            referenceSampleCount: Math.round((old?.referenceSampleCount ?? vector.referenceSampleCount) * (1 - weight) + vector.referenceSampleCount * weight),
            sourceSampleCount: Math.round((old?.sourceSampleCount ?? vector.sourceSampleCount) * (1 - weight) + vector.sourceSampleCount * weight),
            confidence: round((old?.confidence ?? vector.confidence) * (1 - weight) + vector.confidence * weight, 4),
        };
    });
    return {
        vectors,
        hueVsHueCurve: current.hueVsHueCurve.map((value, index) => round((previous.hueVsHueCurve[index] ?? value) * (1 - weight) + value * weight)),
        confidence: round(previous.confidence * (1 - weight) + current.confidence * weight, 4),
    };
}
function isGreenScreenLike(rgb) {
    return rgb.g > 0.22 && rgb.g - Math.max(rgb.r, rgb.b) > 0.1 &&
        rgb.g > rgb.r * 1.25 && rgb.g > rgb.b * 1.12;
}
function isSkinLike(rgb) {
    const maximum = Math.max(rgb.r, rgb.g, rgb.b);
    const minimum = Math.min(rgb.r, rgb.g, rgb.b);
    const hue = (() => {
        if (maximum === minimum)
            return 0;
        let sector = maximum === rgb.r
            ? (rgb.g - rgb.b) / (maximum - minimum)
            : maximum === rgb.g
                ? 2 + (rgb.b - rgb.r) / (maximum - minimum)
                : 4 + (rgb.r - rgb.g) / (maximum - minimum);
        sector = (sector * 60 + 360) % 360;
        return sector;
    })();
    const colorSaturation = saturation(rgb);
    return hue >= 5 && hue <= 55 && colorSaturation >= 0.12 && colorSaturation <= 0.75 &&
        rgb.r >= rgb.g * 0.9 && rgb.g >= rgb.b * 0.75;
}
function pixel(frame, normalizedX, normalizedY) {
    const x = Math.min(frame.width - 1, Math.floor(normalizedX * frame.width));
    const y = Math.min(frame.height - 1, Math.floor(normalizedY * frame.height));
    const offset = (y * frame.width + x) * 4;
    return {
        r: (frame.data[offset] ?? 0) / 255,
        g: (frame.data[offset + 1] ?? 0) / 255,
        b: (frame.data[offset + 2] ?? 0) / 255,
    };
}
function histogramSimilarity(first, second) {
    return new PerceptualHistogram(first.length, first)
        .similarity(new PerceptualHistogram(second.length, second));
}
function blendArray(previous, current, currentWeight) {
    return current.map((value, index) => round((previous[index] ?? value) * (1 - currentWeight) + value * currentWeight));
}
function resolveColorManagement(options) {
    const requestedReference = options?.referenceInputColorSpace ?? 'sRGB';
    const requestedSource = options?.sourceInputColorSpace ?? 'sRGB';
    const requestedWorking = options?.workingColorSpace ?? 'OKLab-analysis-from-sRGB';
    const outputColorSpace = options?.outputColorSpace ?? null;
    const transformWarnings = [];
    let referenceCameraTransformStatus = null;
    let sourceCameraTransformStatus = null;
    const referenceInputColorSpace = getColorSpace(requestedReference) ? requestedReference : 'sRGB';
    const sourceInputColorSpace = getColorSpace(requestedSource) ? requestedSource : 'sRGB';
    let workingColorSpace = requestedWorking;
    if (!getColorSpace(requestedReference)) {
        transformWarnings.push(`Unsupported reference input color space: ${requestedReference}. Falling back to sRGB.`);
    }
    if (!getColorSpace(requestedSource)) {
        transformWarnings.push(`Unsupported source input color space: ${requestedSource}. Falling back to sRGB.`);
    }
    if (requestedWorking !== 'OKLab-analysis-from-sRGB' && !getColorSpace(requestedWorking)) {
        transformWarnings.push(`Unsupported working color space: ${requestedWorking}. Falling back to OKLab-analysis-from-sRGB.`);
        workingColorSpace = 'OKLab-analysis-from-sRGB';
    }
    if (outputColorSpace && !getColorSpace(outputColorSpace)) {
        transformWarnings.push(`Unsupported output color space: ${outputColorSpace}. Output transform metadata only.`);
    }
    if (options?.referenceInputTransferFunction && !isSupportedTransferFunction(options.referenceInputTransferFunction)) {
        transformWarnings.push(`Unsupported reference input transfer function: ${options.referenceInputTransferFunction}. Falling back to color-space default.`);
    }
    if (options?.sourceInputTransferFunction && !isSupportedTransferFunction(options.sourceInputTransferFunction)) {
        transformWarnings.push(`Unsupported source input transfer function: ${options.sourceInputTransferFunction}. Falling back to color-space default.`);
    }
    if (options?.outputTransferFunction && !isSupportedTransferFunction(options.outputTransferFunction)) {
        transformWarnings.push(`Unsupported output transfer function: ${options.outputTransferFunction}. Output transform metadata only.`);
    }
    const cameraWarnings = [
        ['reference', options?.referenceCameraProfile],
        ['source', options?.sourceCameraProfile],
    ];
    cameraWarnings.forEach(([role, profileId]) => {
        if (!profileId)
            return;
        const profile = getCameraProfile(profileId);
        if (!profile) {
            if (role === 'reference')
                referenceCameraTransformStatus = 'unsupported';
            else
                sourceCameraTransformStatus = 'unsupported';
            transformWarnings.push(`Unsupported ${role} camera profile: ${profileId}. No camera transform was applied.`);
        }
        else {
            const status = profile.transformStatus;
            if (role === 'reference')
                referenceCameraTransformStatus = status;
            else
                sourceCameraTransformStatus = status;
            if (status === 'metadata-only') {
                transformWarnings.push(`${profile.name}: ${profile.warning} Transform status: metadata-only.`);
            }
            else if (status === 'unsupported') {
                transformWarnings.push(`${profile.name}: camera transform is unsupported. No camera transform was applied.`);
            }
        }
    });
    return {
        referenceInputColorSpace,
        sourceInputColorSpace,
        workingColorSpace,
        outputColorSpace,
        referenceCameraProfile: options?.referenceCameraProfile ?? null,
        sourceCameraProfile: options?.sourceCameraProfile ?? null,
        referenceCameraTransformStatus,
        sourceCameraTransformStatus,
        transformWarnings,
        pceColorManaged: workingColorSpace !== 'OKLab-analysis-from-sRGB' ||
            referenceInputColorSpace !== 'sRGB' ||
            sourceInputColorSpace !== 'sRGB' ||
            Boolean(options?.referenceInputTransferFunction || options?.sourceInputTransferFunction),
    };
}
/** Deterministic reference/source perceptual color comparison pipeline. */
export class PCEAnalyzer {
    id = 'pce';
    label = 'Perceptual Color Engine';
    hue = new HueDistributionAnalyzer();
    saturation = new SaturationDistributionAnalyzer();
    luminance = new LuminanceDistributionAnalyzer();
    corrections = new CorrectionEstimator();
    previousCorrections;
    reset() {
        this.previousCorrections = undefined;
    }
    async analyze(input) {
        if (input.options?.palettePresetId)
            input = { ...input, referenceFrame: createPCEPaletteFrame(input.options.palettePresetId),
                options: { ...input.options, referenceInputColorSpace: 'sRGB', referenceInputTransferFunction: 'sRGB', referenceCameraProfile: undefined } };
        const [referenceFrame, sourceFrame] = await Promise.all([
            frameToImageData(input.referenceFrame),
            frameToImageData(input.sourceFrame),
        ]);
        const colorManagement = resolveColorManagement(input.options);
        const transforms = createWorkingTransforms(input.options, colorManagement);
        colorManagement.transformWarnings.push(...transforms.warnings);
        if (input.options?.palettePresetId) {
            const preset = getPCEPalettePreset(input.options.palettePresetId);
            const working = colorManagement.workingColorSpace === 'OKLab-analysis-from-sRGB' ? 'sRGB' : colorManagement.workingColorSpace;
            const display = working === 'sRGB' ? undefined : createColorTransform({ from: working, to: 'sRGB' });
            const sourceSamples = [];
            const stride = Math.max(1, Math.ceil(sourceFrame.width * sourceFrame.height / sampleBudget(input.options)), Math.floor(input.options.sampling?.stride ?? 1));
            for (let p = 0; p < sourceFrame.width * sourceFrame.height; p += stride) {
                const i = p * 4;
                if (!sourceFrame.data[i + 3])
                    continue;
                const raw = { r: sourceFrame.data[i] / 255, g: sourceFrame.data[i + 1] / 255, b: sourceFrame.data[i + 2] / 255 };
                const rgb = transforms.source?.rgb(raw) ?? raw;
                sourceSamples.push(display?.rgb(rgb) ?? rgb);
            }
            if (!sourceSamples.length)
                throw new Error('PCE palette matching requires opaque source pixels.');
            let paletteCorrection = input.options.paletteCorrection ?? createPaletteColorCorrection(sourceSamples, preset.colors.map(([r, g, b]) => ({ r: r / 255, g: g / 255, b: b / 255 })), {
                monochrome: preset.id === 'black-and-white', matchLightness: preset.id === 'high-contrast' || preset.id === 'low-contrast',
            });
            if (preset.id === 'black-and-white')
                paletteCorrection = { ...paletteCorrection, monochrome: true };
            const paletteAnalysis = measurePaletteMatch(sourceSamples, paletteCorrection);
            const referenceColors = preset.colors.map(([r, g, b]) => ({ r: r / 255, g: g / 255, b: b / 255 }));
            const hueMatchScore = histogramSimilarity(this.hue.analyze(referenceColors).histogram, this.hue.analyze(sourceSamples).histogram);
            const saturationMatchScore = histogramSimilarity(this.saturation.analyze(referenceColors).histogram, this.saturation.analyze(sourceSamples).histogram);
            const luminanceMatchScore = histogramSimilarity(this.luminance.analyze(referenceColors).histogram, this.luminance.analyze(sourceSamples).histogram);
            // Histogram readouts remain descriptive. The creative audit compares
            // palette chroma adherence, never skin/neutral pixel correspondence.
            return { overallMatchScore: round(paletteAnalysis.colorAdherenceScore), hueMatchScore: round(hueMatchScore),
                saturationMatchScore: round(saturationMatchScore), luminanceMatchScore: round(luminanceMatchScore),
                confidence: round(Math.min(0.9, 0.5 + sourceSamples.length / 4000)), paletteAnalysis,
                suggestedCorrections: { temperatureShift: 0, tintShift: 0, exposureShift: 0, saturationShift: preset.id === 'black-and-white' ? -100 : 0,
                    contrastShift: 0, toneCurve: [0, 1], hueVsHue: Array(12).fill(0), hueVsSat: Array(12).fill(0), paletteCorrection },
                colorManagement: { inputColorSpace: { reference: 'sRGB', source: colorManagement.sourceInputColorSpace },
                    workingColorSpace: colorManagement.workingColorSpace, outputColorSpace: colorManagement.outputColorSpace,
                    cameraProfile: { reference: null, source: colorManagement.sourceCameraProfile },
                    transformStatus: { referenceCameraProfile: null, sourceCameraProfile: colorManagement.sourceCameraTransformStatus },
                    transformWarnings: [...new Set([...colorManagement.transformWarnings, ...(display?.warnings ?? [])])], pceColorManaged: colorManagement.pceColorManaged },
                diagnostics: [`Creative palette mode: ${preset.name}. Score is palette color adherence, not scene similarity.`,
                    'Palette applies globally, including skin. Image-reference skin/neutral correspondence and green-screen exclusion do not override an explicit palette.',
                    'A palette is not a camera white-point estimate. Use CUBE export; ASC CDL cannot represent this tone-conditioned color mapping.',
                    ...(paletteCorrection.monochrome ? ['Black & White: output saturation is exactly zero.'] : ['Continuous OKLab palette colorization preserves source lightness; out-of-gamut chroma is compressed.']),
                    ...colorManagement.transformWarnings] };
        }
        const samples = this.sampleFrames(referenceFrame, sourceFrame, input.options, colorManagement, transforms, input.semanticContext);
        if (!samples.reference.length) {
            throw new Error('PCE could not find usable pixels after applying the selected masks.');
        }
        const referenceHue = this.hue.analyze(samples.reference);
        const sourceHue = this.hue.analyze(samples.source);
        const referenceSaturation = this.saturation.analyze(samples.reference);
        const sourceSaturation = this.saturation.analyze(samples.source);
        const referenceLuminance = this.luminance.analyze(samples.reference);
        const sourceLuminance = this.luminance.analyze(samples.source);
        const hueMatchScore = histogramSimilarity(referenceHue.histogram, sourceHue.histogram);
        const saturationMatchScore = histogramSimilarity(referenceSaturation.histogram, sourceSaturation.histogram);
        const luminanceMatchScore = histogramSimilarity(referenceLuminance.histogram, sourceLuminance.histogram);
        const perceptualMatchScore = Math.exp(-samples.averagePerceptualDistance * 4.5);
        let overallMatchScore = clamp01(hueMatchScore * 0.24 +
            saturationMatchScore * 0.2 +
            luminanceMatchScore * 0.3 +
            perceptualMatchScore * 0.26);
        const dimensionAgreement = Math.min(referenceFrame.width / sourceFrame.width, sourceFrame.width / referenceFrame.width, referenceFrame.height / sourceFrame.height, sourceFrame.height / referenceFrame.height);
        const confidence = clamp01(Math.min(1, samples.reference.length / 2_000) * 0.55 +
            dimensionAgreement * 0.25 +
            Math.min(1, referenceHue.sampleCount / Math.max(1, samples.reference.length * 0.25)) * 0.2);
        let suggestedCorrections = this.corrections.estimate(samples.reference, samples.source);
        const tonalAnalysis = input.options?.matchTonalRanges ? new TonalDistributionAnalyzer().analyze(samples.reference, samples.source, {
            workingColorSpace: colorManagement.workingColorSpace === 'OKLab-analysis-from-sRGB' ? 'sRGB' : colorManagement.workingColorSpace,
            maxSamples: input.options.sampling?.quality === 'realtime' ? 2_000 : input.options.sampling?.quality === 'quality' ? 12_000 : 6_000,
            matchSixVectorColors: input.options.matchSixVectorColors,
        }) : undefined;
        if (tonalAnalysis) {
            suggestedCorrections.tonalCorrection = createTonalCorrection(tonalAnalysis);
            if (tonalAnalysis.matchScore !== null)
                overallMatchScore = overallMatchScore * 0.8 + tonalAnalysis.matchScore * 0.2;
        }
        if (input.options?.matchSixVectorColors === false) {
            suggestedCorrections = {
                ...suggestedCorrections,
                colorVectorMatch: undefined,
                hueVsHue: Array(12).fill(0),
                hueVsSat: Array(12).fill(0),
            };
        }
        if (input.options?.matchMidtoneSkin === false) {
            suggestedCorrections = {
                ...suggestedCorrections,
                skinToneMatch: undefined,
            };
        }
        const distanceLabel = 'Average OKLab distance';
        const diagnostics = [
            `Compared ${samples.reference.length} deterministic pixel samples.`,
            `${distanceLabel}: ${round(samples.averagePerceptualDistance, 5)}.`,
            `PCE working color space: ${colorManagement.workingColorSpace}.`,
        ];
        diagnostics.push(...colorManagement.transformWarnings);
        if (tonalAnalysis)
            diagnostics.push(`Soft tonal analysis used ${tonalAnalysis.referenceSampleCount}/${tonalAnalysis.sourceSampleCount} working-space samples.`, 'Tonal levels are sRGB-encoded linear-light luminance, not physical light-source or object correspondence estimates.', ...tonalAnalysis.warnings);
        if (input.options?.ignoreGreenscreen) {
            diagnostics.push(`Ignored ${samples.ignoredGreenPixels} green-screen-like sample pairs.`);
        }
        if (input.options?.protectSkin) {
            diagnostics.push(`Reduced correction weight for ${samples.protectedSkinPixels} skin-like sample pairs.`);
        }
        if (input.options?.matchMidtoneSkin === false) {
            diagnostics.push('Skin/midtone CDL matching was disabled by user option.');
        }
        else if (suggestedCorrections.skinToneMatch) {
            diagnostics.push(`Skin/midtone match used ${suggestedCorrections.skinToneMatch.reference.sampleCount}/${suggestedCorrections.skinToneMatch.source.sampleCount} reference/source samples at ${round(suggestedCorrections.skinToneMatch.confidence)} confidence.`);
        }
        if (referenceFrame.width !== sourceFrame.width || referenceFrame.height !== sourceFrame.height) {
            diagnostics.push('Reference and source dimensions differ; normalized nearest-neighbor sampling was used.');
        }
        if (input.options?.temporalStability && this.previousCorrections) {
            const weight = 0.35;
            suggestedCorrections = {
                temperatureShift: round(this.previousCorrections.temperatureShift * (1 - weight) + suggestedCorrections.temperatureShift * weight),
                tintShift: round(this.previousCorrections.tintShift * (1 - weight) + suggestedCorrections.tintShift * weight),
                exposureShift: round(this.previousCorrections.exposureShift * (1 - weight) + suggestedCorrections.exposureShift * weight),
                saturationShift: round(this.previousCorrections.saturationShift * (1 - weight) + suggestedCorrections.saturationShift * weight),
                contrastShift: round(this.previousCorrections.contrastShift * (1 - weight) + suggestedCorrections.contrastShift * weight),
                toneCurve: blendArray(this.previousCorrections.toneCurve, suggestedCorrections.toneCurve, weight),
                hueVsHue: input.options?.matchSixVectorColors === false ? Array(12).fill(0) : blendArray(this.previousCorrections.hueVsHue, suggestedCorrections.hueVsHue, weight),
                hueVsSat: input.options?.matchSixVectorColors === false ? Array(12).fill(0) : blendArray(this.previousCorrections.hueVsSat, suggestedCorrections.hueVsSat, weight),
                whitePoint: blendOptionalWhitePoint(this.previousCorrections.whitePoint, suggestedCorrections.whitePoint, weight),
                blackPoint: blendOptionalBlackPoint(this.previousCorrections.blackPoint, suggestedCorrections.blackPoint, weight),
                saturationMatch: blendOptionalSaturationMatch(this.previousCorrections.saturationMatch, suggestedCorrections.saturationMatch, weight),
                skinToneMatch: input.options?.matchMidtoneSkin === false ? undefined : blendOptionalSkinToneMatch(this.previousCorrections.skinToneMatch, suggestedCorrections.skinToneMatch, weight),
                colorVectorMatch: input.options?.matchSixVectorColors === false ? undefined : blendOptionalColorVectorMatch(this.previousCorrections.colorVectorMatch, suggestedCorrections.colorVectorMatch, weight),
                // Soft tonal partitions are measured anew, rather than mixing stale range populations.
                tonalCorrection: suggestedCorrections.tonalCorrection,
            };
            diagnostics.push('Temporal correction smoothing was applied.');
        }
        this.previousCorrections = suggestedCorrections;
        return {
            overallMatchScore: round(overallMatchScore),
            hueMatchScore: round(hueMatchScore),
            saturationMatchScore: round(saturationMatchScore),
            luminanceMatchScore: round(luminanceMatchScore),
            confidence: round(confidence),
            suggestedCorrections,
            colorManagement: {
                inputColorSpace: {
                    reference: colorManagement.referenceInputColorSpace,
                    source: colorManagement.sourceInputColorSpace,
                },
                workingColorSpace: colorManagement.workingColorSpace,
                outputColorSpace: colorManagement.outputColorSpace,
                cameraProfile: {
                    reference: colorManagement.referenceCameraProfile,
                    source: colorManagement.sourceCameraProfile,
                },
                transformStatus: {
                    referenceCameraProfile: colorManagement.referenceCameraTransformStatus,
                    sourceCameraProfile: colorManagement.sourceCameraTransformStatus,
                },
                transformWarnings: colorManagement.transformWarnings,
                pceColorManaged: colorManagement.pceColorManaged,
            },
            diagnostics,
            tonalAnalysis,
        };
    }
    sampleFrames(reference, source, options, colorManagement, transforms, semanticContext) {
        const targetSamples = Math.min(sampleBudget(options), Math.max(reference.width * reference.height, source.width * source.height));
        const columns = Math.max(1, Math.round(Math.sqrt(targetSamples * reference.width / Math.max(1, reference.height))));
        const rows = Math.max(1, Math.ceil(targetSamples / columns));
        const rowStride = options?.sampling?.stride ? Math.max(1, Math.floor(options.sampling.stride)) : 1;
        const referenceSamples = [];
        const sourceSamples = [];
        let totalDistance = 0;
        let ignoredGreenPixels = 0;
        let protectedSkinPixels = 0;
        const distance = createPerceptualMetric(colorManagement.workingColorSpace);
        for (let row = 0; row < rows; row += rowStride) {
            for (let column = 0; column < columns; column += rowStride) {
                const normalizedX = (column + 0.5) / columns;
                const normalizedY = (row + 0.5) / rows;
                const referencePixel = pixel(reference, normalizedX, normalizedY);
                const sourcePixel = pixel(source, normalizedX, normalizedY);
                if (options?.ignoreGreenscreen && (isGreenScreenLike(referencePixel) || isGreenScreenLike(sourcePixel))) {
                    ignoredGreenPixels++;
                    continue;
                }
                const referenceSkin = semanticSkinWeight(semanticContext?.reference, normalizedX, normalizedY);
                const sourceSkin = semanticSkinWeight(semanticContext?.source, normalizedX, normalizedY);
                const skin = options?.protectSkin && ((referenceSkin === undefined ? isSkinLike(referencePixel) : referenceSkin >= 0.65) ||
                    (sourceSkin === undefined ? isSkinLike(sourcePixel) : sourceSkin >= 0.65));
                const weight = skin ? 0.25 : 1;
                if (skin)
                    protectedSkinPixels++;
                const referenceWorking = transforms.reference?.rgb(referencePixel) ?? referencePixel;
                const sourceWorking = transforms.source?.rgb(sourcePixel) ?? sourcePixel;
                referenceSamples.push({ ...referenceWorking, weight });
                sourceSamples.push({ ...sourceWorking, weight });
                totalDistance += distance(referenceWorking, sourceWorking);
            }
        }
        return {
            reference: referenceSamples,
            source: sourceSamples,
            averagePerceptualDistance: totalDistance / Math.max(1, referenceSamples.length),
            ignoredGreenPixels,
            protectedSkinPixels,
        };
    }
}
//# sourceMappingURL=PCEAnalyzer.js.map